import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Box, Button, TextField, Paper, Typography, InputAdornment } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Spinner from "../../../Component/spinner/spinner";
import {
  useGetResourceCategoryByIdQuery,
  useSaveResourceCategoryMutation,
  useUpdateResourceCategoryMutation
} from "../../../app/services/resourceCategoryServices";

export default function AddEditResourceCategory() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const currentLocation = useLocation();
  var currentPath = currentLocation.pathname.split('/');

  const { data: categoryData, isLoading: isFetching } = useGetResourceCategoryByIdQuery(id, { skip: !isEditMode });
  const [saveCategory, { isLoading: isSaving }] = useSaveResourceCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] = useUpdateResourceCategoryMutation();

  const loading = isFetching || isSaving || isUpdating;

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
    },
  });

  useEffect(() => {
    if (isEditMode && categoryData?.data) {
      setValue("name", categoryData.data.name);
      setValue("description", categoryData.data.description || "");
    }
  }, [isEditMode, categoryData, setValue]);

  const onSubmit = async (formData) => {
    try {
      let data;
      if (isEditMode) {
        data = await updateCategory({ id, data: formData }).unwrap();
      } else {
        data = await saveCategory(formData).unwrap();
      }

      if (data.status) {
        toast.success(data.message || `Category ${isEditMode ? "updated" : "created"} successfully!`);
        navigate(`/${currentPath[1]}/resource-category`);
      } else {
        toast.error(data.err?.message || "Operation failed");
      }
    } catch (error) {
      toast.error("Error submitting form");
    }
  };

  return (
    <Box>
      <div className="pt-5">
        <div className="chapter-heading">
          <h2 className="text-center title-font text-light py-3 mt-1">{isEditMode ? "Update Resource Category" : "Add Resource Category"}</h2>
        </div>
      </div>
      <div className='p-md-5 p-3 overallPadding'>
        <div className="text-end w-100">
          <Button variant="contained" className="menus-color poppins-regular" style={{ color: "white", marginBottom: "15px" }} onClick={() => window.history.back()}>
            Back
          </Button>
        </div>
        <Paper sx={{ p: 3, mt: 2 }}>
          {isFetching && loading ? (
            <Box display="flex" justifyContent="center"><Spinner /></Box>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box mb={2}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: "Name is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      label={
                        <Typography className="text-dark">
                          Category Name <span style={{ color: "red" }}>*</span>
                        </Typography>
                      }
                      error={!!errors.name}
                      helperText={errors.name ? errors.name.message : ""}
                      placeholder="Category Name"
                    />
                  )}
                />
              </Box>
              <Box mb={2}>
                <Controller
                  name="description"
                  control={control}
                  rules={{
                    maxLength: { value: 250, message: 'Please enter a description less than 250 letters' }
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      rows={4}
                      id="startDate"
                      label={
                        <Typography className="text-dark">
                          Description
                        </Typography>
                      }
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end" sx={{ top: "100%" }}>
                            <p className="menus-green-color"> {250 - (field.value ? field.value.length : 0)}/250</p>
                          </InputAdornment>
                        ),
                      }}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Box>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button variant="outlined" onClick={() => navigate("/admin/resource-category")} disabled={loading}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary" disabled={loading}>
                  {isEditMode ? "Update" : "Save"}
                </Button>
              </Box>
            </form>
          )}
        </Paper>
      </div>
    </Box>
  );
}
